import type { ComponentProps } from "react"

import {
  Button as UUIButton,
  type ButtonProps,
} from "@/components/ui-kit/untitled/base/buttons/button"

import { cx } from "@uui/utils/cx"

export type { ButtonProps }

/*
 * One button family at one weight. The primary is the one filled button per
 * attended region (page, panel, dialog, step): an open dialog or rack takes
 * the fill while it is open, and a per-row or repeater action never does. It
 * reads its fill from the token (`bg-brand-solid`, print.800, hover print.900)
 * with stone.50 text and icons; the secondary is paper on a stone.400 ring,
 * which is the kit's own secondary under this theme; a link is print. Labels
 * are 600. Heights follow the system: 28 / 32 / 36 / 44,
 * where 44 is the floor for anything a coordinator taps on a phone, and the
 * icon box is 14px at 28, 16px above, so a 24-grid glyph sits on the cap height.
 * While loading the fill holds at print.800: the ring says busy, and the kit's
 * step to the hover fill read as hovered.
 *
 * The Do glyph (`DoGlyph` from ui/wordmark) leads a relationship-changing
 * action such as "Plan a run"; Save, Discard and Next stay plain.
 */
const heights = {
  sm: "h-7 px-2.5 py-0 text-xs font-semibold *:data-icon:size-3.5 data-icon-only:p-0 data-icon-only:w-7",
  md: "h-8 px-3 py-0 text-sm font-semibold *:data-icon:size-4 data-icon-only:p-0 data-icon-only:w-8",
  lg: "h-9 px-3.5 py-0 text-sm font-semibold *:data-icon:size-4 data-icon-only:p-0 data-icon-only:w-9",
  xl: "h-11 px-4 py-0 text-md font-semibold *:data-icon:size-4 data-icon-only:p-0 data-icon-only:w-11",
} as const

const tones: Partial<Record<NonNullable<ButtonProps["color"]>, string>> = {
  primary:
    "text-primary_on-brand before:hidden *:data-icon:text-primary_on-brand hover:*:data-icon:text-primary_on-brand data-loading:bg-brand-solid",
  secondary: "hover:bg-primary_hover",
  "primary-destructive": "text-primary_on-brand before:hidden",
  "secondary-destructive": "bg-transparent",
}

type Props = ComponentProps<typeof UUIButton>

export function Button({
  size = "md",
  color = "primary",
  className,
  ...rest
}: Props) {
  return (
    <UUIButton
      size={size}
      color={color}
      className={cx(heights[size], tones[color], className)}
      {...rest}
    />
  )
}
